import styled, { css } from 'styled-components';
import { StyledComponentsPropWithTheme } from 'src/commons/components/ChromeApp/ChromeAppUtils';
import {
  Button,
  isMobileStyleMixin as isMobile,
  isMobileStyleMixin,
  Link,
  SearchInput,
  styleMixinInvertedFocusOutline,
  Text,
  TextButton,
} from '@amzn/storm-ui';

export const accountSelectionListId = `top-bar-utilities-acc-selector`;

interface AccountDropdownButtonProps extends StyledComponentsPropWithTheme {
  active?: boolean;
  status?: 'inactive' | 'active';
}

export const containerWithBorderTopMixin = css`
  border-top: 1px solid #e7e7e7;
`;

export const containerWithBorderBottomMixin = css`
  border-bottom: 1px solid #e7e7e7;
`;

export const IA2AccountDropdownButton = styled(
  Button,
)<AccountDropdownButtonProps>`
  background-color: transparent;
  border: none;
  padding: ${({ theme }) =>
    `${theme.spacing.xxxsmall.default} ${theme.spacing.xxsmall.default}`};

  ${isMobile(css`
    width: 100%;
    padding: ${({ theme }) => theme.spacing.xxxsmall.mobile};
    justify-content: flex-start;
  `)};

  ${({ active }) =>
    active &&
    css`
      background: ${({ theme }) => theme.color.gray[800]};
    `};

  &:disabled {
    background-color: transparent;
  }

  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.color.gray[1000]};
  }

  &&:focus-visible {
    ${styleMixinInvertedFocusOutline};
  }
`;

export const AccountName = styled.div`
  text-align: right;
  line-height: 16px;
  color: ${({ theme }) => theme.header.utility.icon.foreground};
`;

export const AccountDropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.color.gray[0]};
  width: 350px;
  height: initial;
  border-radius: ${({ theme }) => theme.border.radius.medium};
  overflow: hidden;

  /* Edge */
  @supports (-ms-ime-align: auto) {
    & {
      overflow-x: hidden;
    }
  }
`;

export const AccountDropdownLinksWrapper = styled.div`
  padding: ${({ theme }) => `${theme.spacing.xsmall.default} ${theme.spacing.base.default}`};
}

/* Edge */
@supports (
  -ms-ime-align: auto) {
  & {
    bottom: -18px;
  }
}

${isMobile(css`
  margin-top: ${({ theme }) => theme.spacing.xsmall.default};
  padding: ${({ theme }) => theme.spacing.xsmall.default};
  ${containerWithBorderTopMixin}
`)}
`;

export const ManageAccountLinkWrapper = styled.div`
  display: block;
  line-height: initial;

  &:not(:first-child) {
    margin-top: 10px;
  }
`;

export const ManageAccountLink = styled(Link)`
  display: inline;

  && {
    color: ${({ theme }) => theme.link.color.default};
  }
`;

interface AccountsListWrapperProps {
  hasManagerAccounts: boolean;
}

/*
 * The account list wrapper's max height is limited to 398px,
 * if no manager accounts are present. The number is not determined
 * by a specific element.
 */
export const AccountsListWrapper = styled.div<AccountsListWrapperProps>`
  flex-grow: 1;
  height: initial;
  padding-bottom: 14px;

  ${(props) =>
    !props.hasManagerAccounts
      ? `
      max-height: 398px;
      overflow-y: auto;
    `
      : ''};
`;

export const IA2AccountDropdownList = styled.ul`
  margin: 0;
  text-align: left;
  padding: 0;

  ${isMobile(css`
    display: grid;
    gap: ${({ theme }) => theme.spacing.small.default};
  `)};

  &:empty ${AccountsListWrapper} {
    display: none;
  }
`;

export const IA2AccountDropdownSearchInput = styled(SearchInput)`
  & input[type='search'] {
    border: none;
    outline: none;
    box-shadow: none;
    height: auto;
  }

  input[type='search']:focus {
    background-color: ${({ theme }) => theme.color.gray[0]};
  }
`;

export const AccountDropdownAccountName = styled(Text)`
  color: ${({ theme }) => theme.text.color.inverted};
  ${isMobileStyleMixin(css`
    white-space: initial;
    overflow-wrap: anywhere;
  `)}
  margin-bottom: -2px;
`;

export const IA2AccountListItem = styled.li<{
  isSelected: boolean;
}>`
  list-style: none;
  border-radius: ${({ theme }) => theme.border.radius.small};

  ${({ theme, isSelected }) => {
    return isSelected ? `background-color: ${theme.color.gray[100]};` : '';
  }};

  &:hover {
    background-color: ${({ theme }) => theme.color.gray[100]};
  }
`;

export const AccountTypeSectionContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xsmall.default};
`;

export const PaginationButton = styled(TextButton)<{
  extraPaddingAtBottom?: boolean;
}>`
  text-align: left;
  display: block;
  width: 100%;
  padding-inline: 1.25rem;
  margin-block: 0.25rem;

  ${({ extraPaddingAtBottom }) =>
    extraPaddingAtBottom &&
    css`
      padding-bottom: ${({ theme }) => theme.spacing.xsmall.default};
    `};

  ${isMobileStyleMixin(css`
    padding-top: ${({ theme }) => theme.spacing.xsmall.default};
  `)};
`;

export const AccountTypeLabel = styled(Text)<{ withMargin?: boolean }>`
  margin-block: 0;
  margin-inline: ${({ withMargin, theme }) =>
    withMargin === true ? theme.spacing.base.default : ''};
`;

export const AccountTypeLabelContainer = styled.div`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
`;

export const TooltipContentWrapper = styled.div`
  display: inline;
  font-weight: normal;
`;

export const TooltipLink = styled(Link)`
  && {
    color: ${({ theme }) => theme.link.color.default};
    text-decoration: none;
  }
`;
